import { MUISelectCellEditor } from "../components/common/gridSelectCell";
import type { ColDef } from "ag-grid-community";
import { AREA_TYPE_OPTIONS } from "../constants/areaTypes";

export function createSingleSelectColumn(
  field: string,
  label: string,
  values: any[],
  labelKey: string,
  width = 150,
  editable: boolean,
  allowNull: boolean = false,
  nullLabel: string = "(None)",
) {
  // add a class to the column
  const options = values.map((s) => ({ id: s.id, label: s[labelKey] }));
  // Add null option if allowNull is true
  if (allowNull) {
    options.unshift({ id: null, label: nullLabel });
  }

  return {
    field,
    editable,
    headerName: label,
    width,
    cellEditor: MUISelectCellEditor,
    cellEditorParams: {
      values: options,
      originalValues: allowNull
        ? [{ id: null, [labelKey]: nullLabel }, ...values]
        : values, // Pass the original values for proper object construction
      label,
      labelKey,
      multiple: false,
      squareSelectCorners: true,
    },
    valueGetter: (params: any) => {
      if (!params.data) return null;
      const fieldValue = params.data[field];
      // Explicitly check for null/undefined - if null, return null option if allowNull is true
      if (fieldValue === null || fieldValue === undefined) {
        if (allowNull) {
          return { id: null, label: nullLabel };
        }
        return null;
      }
      // If it's already an object, return it; otherwise find it from values
      if (
        typeof fieldValue === "object" &&
        fieldValue.id !== null &&
        fieldValue.id !== undefined
      ) {
        return fieldValue;
      }
      const found = values.find((v) => v.id === fieldValue);
      return found || (allowNull ? { id: null, label: nullLabel } : null);
    },
    valueSetter: (params: any) => {
      // Handle null values
      if (
        params.newValue === null ||
        (params.newValue && params.newValue.id === null)
      ) {
        if (params.data) {
          params.data[field] = null;
          // Also set deal_id to null if this is the deal field
          if (field === "deal") {
            params.data.deal_id = null;
          }
        }
        return true;
      }
      // Handle object values
      if (
        params.newValue &&
        typeof params.newValue === "object" &&
        params.newValue.id !== null
      ) {
        if (params.data) {
          params.data[field] = params.newValue;
          // Also set deal_id if this is the deal field
          if (field === "deal") {
            params.data.deal_id = params.newValue.id;
          }
        }
        return true;
      }
      return false;
    },
    valueFormatter: ({ value }: any) => {
      if (!value || (typeof value === "object" && value.id === null)) return "";
      if (typeof value === "object" && value.id) {
        const found = values.find((s) => s.id === value.id);
        return found?.[labelKey] ?? value[labelKey] ?? "";
      }
      return "";
    },
    comparator: (a: any, b: any) => {
      if (a === null || a === undefined) return -1;
      if (b === null || b === undefined) return 1;
      const labelA = a[labelKey] ?? "";
      const labelB = b[labelKey] ?? "";
      return labelA.localeCompare(labelB);
    },
  };
}

export function createNestedSingleSelectColumn<TItem extends { id: number }>(config: {
  colId: string;
  label: string;
  values: TItem[];
  labelKey: string;
  width?: number;
  editable: boolean;
  allowNull?: boolean;
  getValue: (data: unknown) => TItem | null | undefined;
  setValue: (data: unknown, value: TItem | null) => void;
}): ColDef {
  const {
    colId,
    label,
    values,
    labelKey,
    width = 170,
    editable,
    allowNull = false,
    getValue,
    setValue,
  } = config;

  const base = createSingleSelectColumn(
    colId,
    label,
    values,
    labelKey,
    width,
    editable,
    allowNull,
  );

  const { field: _field, ...baseWithoutField } = base;

  return {
    ...baseWithoutField,
    colId,
    valueGetter: (params: any) => {
      if (!params.data) return allowNull ? { id: null, label: "(None)" } : null;
      const fieldValue = getValue(params.data);
      if (fieldValue === null || fieldValue === undefined) {
        return allowNull ? { id: null, label: "(None)" } : null;
      }
      if (
        typeof fieldValue === "object" &&
        fieldValue.id !== null &&
        fieldValue.id !== undefined
      ) {
        return fieldValue;
      }
      const found = values.find((v) => v.id === (fieldValue as any).id);
      return found || (allowNull ? { id: null, label: "(None)" } : null);
    },
    valueSetter: (params: any) => {
      if (
        params.newValue === null ||
        (params.newValue && params.newValue.id === null)
      ) {
        if (params.data) setValue(params.data, null);
        return true;
      }
      if (
        params.newValue &&
        typeof params.newValue === "object" &&
        params.newValue.id !== null
      ) {
        const match =
          values.find((v) => v.id === params.newValue.id) ?? params.newValue;
        if (params.data) setValue(params.data, match);
        return true;
      }
      return false;
    },
  };
}

export function createMultiSelectColumn(
  field: string,
  label: string,
  values: any[],
  labelKey: string,
  width = 150,
  editable: boolean,
) {
  return {
    field,
    editable,
    width,
    headerName: label,
    cellEditor: MUISelectCellEditor,
    cellEditorParams: {
      values: values.map((s) => ({ id: s.id, label: s[labelKey] })),
      originalValues: values, // Pass the original values for proper object construction
      label,
      labelKey,
      multiple: true,
    },
    valueFormatter: ({ value }: any) => {
      if (!Array.isArray(value)) return "";
      return value.map((obj) => obj[labelKey]).join(", ");
    },
  };
}

/**
 * Dropdown for plain-string fields (e.g. currency, area type).
 * Row data stays as a primitive string so PUT bodies are clean.
 */
export function createStringSelectColumn(
  field: string,
  label: string,
  values: any[],
  width = 150,
  editable: boolean,
): ColDef {
  return {
    field,
    editable,
    headerName: label,
    width,
    cellEditor: MUISelectCellEditor,
    cellEditorParams: {
      values: values.map((s) => ({ id: s, label: s })),
      label,
      multiple: false,
    },
    valueGetter: (params: any) => {
      const v = params.data?.[field];
      if (v == null || v === "") return null;
      if (typeof v === "object") {
        const id = v.id ?? v.label ?? "";
        return id ? { id, label: String(id) } : null;
      }
      return { id: v, label: String(v) };
    },
    valueSetter: (params: any) => {
      if (!params.data) return false;
      const nv = params.newValue;
      const id =
        nv == null
          ? null
          : typeof nv === "object"
            ? (nv.id ?? nv.label ?? null)
            : nv;
      params.data[field] = id == null || id === "" ? null : String(id);
      return true;
    },
    valueFormatter: ({ value }: any) => {
      if (value == null) return "";
      if (typeof value === "string") return value;
      if (typeof value === "object") return value.label || value.id || "";
      return String(value);
    },
  };
}

/**
 * Dropdown for object fields stored as a one-element array (e.g. asset.sector / asset.country).
 * Row data is kept as `[{...}]` and a sibling `*_id` field is updated for the API.
 */
export function createObjectArraySelectColumn(
  field: string,
  label: string,
  values: any[],
  labelKey: string,
  width = 150,
  editable: boolean,
  idField?: string,
): ColDef {
  return {
    field,
    editable,
    headerName: label,
    width,
    cellEditor: MUISelectCellEditor,
    cellEditorParams: {
      values: values.map((s) => ({ id: s.id, label: s[labelKey] })),
      originalValues: values,
      label,
      labelKey,
      multiple: false,
      squareSelectCorners: true,
    },
    valueGetter: (params: any) => {
      const v = params.data?.[field];
      const obj = Array.isArray(v) ? v[0] : v;
      if (!obj || typeof obj !== "object" || obj.id == null) return null;
      return { id: obj.id, label: obj[labelKey] ?? String(obj.id) };
    },
    valueSetter: (params: any) => {
      if (!params.data) return false;
      const nv = params.newValue;
      const id =
        nv == null
          ? null
          : typeof nv === "object"
            ? (nv.id ?? null)
            : nv;
      if (id == null) {
        params.data[field] = [];
        if (idField) params.data[idField] = null;
        return true;
      }
      const original = values.find((o: any) => o.id === id);
      params.data[field] = original ? [original] : [{ id, [labelKey]: nv?.label ?? String(id) }];
      if (idField) params.data[idField] = id;
      return true;
    },
    valueFormatter: ({ value }: any) => {
      if (!value) return "";
      if (typeof value === "object") return value.label || value.id || "";
      return String(value);
    },
  };
}

/** Text or number field stored on a nested object (e.g. lost_to_lender_details). */
export function createNestedScalarColumn(config: {
  colId: string;
  label: string;
  width?: number;
  editable: boolean;
  useNumberEditor?: boolean;
  getValue: (data: unknown) => string | number | null | undefined;
  setValue: (data: unknown, value: string | number | null) => void;
}): ColDef {
  return {
    colId: config.colId,
    field: config.colId,
    headerName: config.label,
    width: config.width ?? 130,
    editable: config.editable,
    cellEditor: config.useNumberEditor ? "agNumberCellEditor" : undefined,
    valueGetter: (params) => {
      const value = config.getValue(params.data);
      if (value == null || value === "") return "";
      return value;
    },
    valueSetter: (params) => {
      if (!params.data) return false;
      const raw = params.newValue;
      let next: string | number | null;
      if (config.useNumberEditor) {
        next = raw === "" || raw == null ? null : Number(raw);
        if (typeof next === "number" && Number.isNaN(next)) next = null;
      } else {
        const trimmed = raw == null ? "" : String(raw).trim();
        next = trimmed === "" ? null : trimmed;
      }
      config.setValue(params.data, next);
      return true;
    },
  };
}

/** String dropdown stored on a nested object (e.g. winning lender currency). */
export function createNestedStringSelectColumn(config: {
  colId: string;
  label: string;
  values: string[];
  width?: number;
  editable: boolean;
  getValue: (data: unknown) => string | null | undefined;
  setValue: (data: unknown, value: string | null) => void;
}): ColDef {
  const base = createStringSelectColumn(
    config.colId,
    config.label,
    config.values,
    config.width ?? 100,
    config.editable,
  );

  const { field: _field, ...baseWithoutField } = base;

  return {
    ...baseWithoutField,
    colId: config.colId,
    valueGetter: (params: any) => {
      const value = config.getValue(params.data);
      if (value == null || value === "") return null;
      return { id: value, label: value };
    },
    valueSetter: (params: any) => {
      if (!params.data) return false;
      const nv = params.newValue;
      const id =
        nv == null
          ? null
          : typeof nv === "object"
            ? (nv.id ?? nv.label ?? null)
            : nv;
      config.setValue(
        params.data,
        id == null || id === "" ? null : String(id),
      );
      return true;
    },
  };
}

/** Area type uses the same MUI string dropdown as Pipeline (single-click open). */
export function createAreaTypeSelectColumn(
  editMode: boolean,
  width = 100,
): ColDef {
  return createStringSelectColumn(
    "area_type",
    "Area Type",
    [...AREA_TYPE_OPTIONS],
    width,
    editMode,
  );
}
